import * as React from "react";
import { Dispatch, SetStateAction } from "react";
import { CheckIcon, ChevronDown, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { Separator } from "../../components/ui/separator";
import { Label } from "components/ui/label";

type SetState<T> = Dispatch<SetStateAction<T>>;
type SelectOption = {
  value: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
};

export interface InputSelectProvided {
  options: SelectOption[];
  onValueChange?: (v: string) => void;
  placeholder: string;
  disabled: boolean;
  selectedValue: string;
  setSelectedValue: SetState<string>;
  isPopoverOpen: boolean;
  setIsPopoverOpen: SetState<boolean>;
  onOptionSelect: (v: string) => void;
  onClearAllOptions: () => void;
}

type Props = {
  options: SelectOption[];
  label?: string;
  value?: string;
  onValueChange?: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
};

export const InputSelect = ({
  options,
  label,
  value = "",
  onValueChange,
  placeholder = "Selecione...",
  disabled = false,
}: Props) => {
  const [selectedValue, setSelectedValue] = React.useState<string>(value);
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);

  const onOptionSelect = (option: string) => {
    setSelectedValue(option);
    onValueChange?.(option);
    setIsPopoverOpen(false);
  };

  const onClearAllOptions = () => {
    setSelectedValue("");
    onValueChange?.("");
    setIsPopoverOpen(false);
  };

  React.useEffect(() => {
    if (isPopoverOpen && value !== selectedValue) {
      setSelectedValue(value);
    }
  }, [isPopoverOpen]);

  return (
    <div className="flex flex-col">
      {label && <Label className="px-1 text-brand-text">{label}</Label>}
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild className="">
          <InputSelectTrigger
            options={options}
            className="border !border-brand-text"
            placeholder={placeholder}
            disabled={disabled}
            selectedValue={selectedValue}
            setSelectedValue={setSelectedValue}
            isPopoverOpen={isPopoverOpen}
            setIsPopoverOpen={setIsPopoverOpen}
            onOptionSelect={onOptionSelect}
            onClearAllOptions={onClearAllOptions}
          />
        </PopoverTrigger>
        <PopoverContent
          className={cn(
            "w-auto z-[1001] bg-brand-sidebar border-none p-4 rounded-3xl font-semibold text-brand-cardtext",
          )}
          align="start"
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
        >
          <Command>
            <CommandInput placeholder="Pesquisar..." className="outline-none" />
            <CommandList className="max-h-[unset] overflow-y-hidden">
              <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>
              <CommandGroup className="max-h-[20rem] overflow-y-auto &::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {options.map((option) => {
                  const isSelected = selectedValue === option.value;
                  return (
                    <CommandItem
                      key={option.value}
                      onSelect={() => onOptionSelect(option.value)}
                      className="cursor-pointer hover:bg-white hover:text-brand-text"
                    >
                      <div
                        className={cn(
                          "flex h-4 w-4 items-center justify-center",
                          isSelected ? "text-primary" : "invisible",
                        )}
                      >
                        <CheckIcon className="w-4 h-4" />
                      </div>
                      {option.icon && (
                        <option.icon className="w-4 h-4 mr-2 text-muted-foreground" />
                      )}
                      <span>{option.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
InputSelect.displayName = "InputSelect";

export const InputSelectTrigger = React.forwardRef<
  HTMLButtonElement,
  InputSelectProvided & {
    className?: string;
    children?: (v: SelectOption) => React.ReactNode;
    style?: React.CSSProperties;
  }
>(
  (
    {
      options,
      // onValueChange,
      placeholder,
      disabled,
      selectedValue,
      // setSelectedValue,
      // isPopoverOpen,
      setIsPopoverOpen,
      // onOptionSelect,
      onClearAllOptions,
      className,
      style,
      children,
    },
    ref,
  ) => {
    const onTogglePopover = () => {
      setIsPopoverOpen((prev) => !prev);
    };

    return (
      <Button
        ref={ref}
        onClick={onTogglePopover}
        variant="outline"
        type="button"
        disabled={disabled}
        className={cn(
          "flex h-11 w-full items-center justify-between p-1 [&_svg]:pointer-events-auto",
          "hover:bg-transparent",
          disabled && "[&_svg]:pointer-events-none",
          className,
        )}
        style={style}
      >
        {selectedValue ? (
          <div className="flex items-center justify-between w-full">
            <div className="flex flex-wrap items-center px-2">
              {[selectedValue].map((value, index) => {
                const option = options.find((o) => o.value === value);

                if (!option) {
                  return <div key={`${index}-${value}`}></div>;
                }

                if (children) {
                  return (
                    <div key={`${index}-${value}`}>{children(option)}</div>
                  );
                }

                return (
                  <div
                    key={`${index}-${value}`}
                    className={cn("text-foreground")}
                  >
                    {option?.icon && (
                      <option.icon className="mr-1 h-3.5 w-3.5" />
                    )}
                    {option?.label}
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between">
              {selectedValue && (
                <>
                  <X
                    className={cn(
                      "mx-1 h-4 cursor-pointer text-muted-foreground",
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      onClearAllOptions();
                    }}
                  />
                  <Separator
                    orientation="vertical"
                    className="flex h-full min-h-6"
                  />
                </>
              )}
              <ChevronDown className="h-4 mx-1 cursor-pointer text-muted-foreground" />
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between w-full mx-auto">
            <span className="mx-3 text-sm text-muted-foreground opacity-50">
              {placeholder}
            </span>
            <ChevronDown className="h-4 mx-1 cursor-pointer text-muted-foreground" />
          </div>
        )}
      </Button>
    );
  },
);
InputSelectTrigger.displayName = "InputSelectTrigger";
